// src/utils/calculations.js

/**
 * Safe number coercion — returns 0 for null/undefined/empty/NaN.
 * @param {*} val
 * @returns {number}
 */
export const num = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Calculate total wastage percentage for an order/product.
 * Formula: buyerDeskCalculation + wastage + wastageOperator + wastageJmi + specificationMC + 
 *          (specification && specification !== "PLANE" ? specificationMakingJmi + specificationMakingOperator : 0)
 * @param {Object} product - Order/product object
 * @returns {number} Total wastage percentage
 */
export const calculateWastage = (product) => {
  const n = num;
  return (
    n(product.buyerDeskCalculation) +
    n(product.wastage) +
    n(product.wastageOperator) +
    n(product.wastageJmi) +
    n(product.specificationMC) +
    (product.specification && product.specification !== "PLANE"
      ? n(product.specificationMakingJmi) + n(product.specificationMakingOperator)
      : 0)
  );
};

/**
 * Calculate set MC with making charges.
 * Formula: setMcBase + setMcBase * ((setMakingJmi + setMakingOperator) / 100)
 * @param {Object} product - Order/product object
 * @returns {number} Set MC value
 */
export const calculateSetMc = (product) => {
  const n = num;
  const setMcBase = n(product.setMc);
  return setMcBase + setMcBase * ((n(product.setMakingJmi) + n(product.setMakingOperator)) / 100);
};

/**
 * Calculate gram MC per gram.
 * Formula: netGramMc + netGramMc * ((gramMakingJmi + gramMakingOperator) / 100)
 * @param {Object} product - Order/product object
 * @returns {number} Gram MC per gram value
 */
export const calculateGramMcPerGram = (product) => {
  const n = num;
  return n(product.netGramMc) + n(product.netGramMc) * ((n(product.gramMakingJmi) + n(product.gramMakingOperator)) / 100);
};

/**
 * Calculate specification MC per gram.
 * Formula: specificationGramRate + specificationGramRate * ((specificationGramRateJmi + specificationGramRateOperator) / 100)
 * @param {Object} product - Order/product object
 * @returns {number} Specification MC per gram value
 */
export const calculateSpecMcPerGram = (product) => {
  const n = num;
  return n(product.specificationGramRate) + n(product.specificationGramRate) * ((n(product.specificationGramRateJmi) + n(product.specificationGramRateOperator)) / 100);
};

/**
 * Calculate fine weight from net weight and wastage percentage.
 * Formula: (netWt * wastage) / 100
 * @param {number} netWt - Net weight
 * @param {number} wastage - Wastage percentage
 * @returns {number} Fine weight (to 3 decimal places)
 */
export const calculateFineWeight = (netWt, wastage) => {
  return formatNumber((netWt * wastage) / 100, 3);
};

/**
 * Calculate total MC from components.
 * Formula: setMc + (gramMcPerGram * netWt) + (specMcPerGram * specWt)
 * @param {{ setMc: number, gramMcPerGram: number, netWt: number, specMcPerGram: number, specWt: number }} params - Calculation parameters
 * @returns {number} Total MC value (to 2 decimal places)
 */
export const calculateTotalMc = ({ setMc, gramMcPerGram, netWt, specMcPerGram, specWt }) => {
  return formatNumber(setMc + gramMcPerGram * netWt + specMcPerGram * specWt, 2);
};

/**
 * Calculate stamp certificate cost for an order.
 * Formula: sum over all detail items of (stampCertificateQtyPerSet * qty * stampCertificateCost)
 * @param {Object} order - Order object
 * @returns {number} Total stamp certificate cost
 */
export const calculateStampCertificateCost = (order) => {
  const n = num;
  if (!order.orderDetail) return 0;
  return Object.values(order.orderDetail).reduce((sum, detail) => {
    return sum + n(detail.stampCertificateQtyPerSet) * n(detail.qty) * n(order.stampCertificateCost);
  }, 0);
};

/**
 * Calculate weights for lot-based products.
 * @param {Object} product - Product object with lotDetails and selectedVariants
 * @returns {Object} Object containing totalGrossWt, totalNetWt, totalFineWt, totalMC, totalSets
 */
export const calculateLotWeights = (product) => {
  const n = num;
  const wastage = calculateWastage(product);
  const setMcBase = n(product.setMc);
  const gramMcPerGram = calculateGramMcPerGram(product);
  const specMcPerGram = calculateSpecMcPerGram(product);
  const setMakingPercent = n(product.setMakingJmi) + n(product.setMakingOperator);
  
  const totals = { totalGrossWt: 0, totalNetWt: 0, totalFineWt: 0, totalMC: 0, totalSets: 0 };
  
  if (Array.isArray(product.lotDetails) && Array.isArray(product.selectedVariants)) {
    product.selectedVariants.forEach((variant) => {
      const lot = product.lotDetails.find((l) => l.size === variant.size);
      if (!lot) return;
      
      const qty = n(variant.quantity);
      const avgNet = n(lot.avgNetWt);
      const avgGross = n(lot.avgGrossWt);
      const avgSpec = n(lot.avgSpecWt);
      
      totals.totalSets += qty;
      totals.totalNetWt += avgNet * qty;
      totals.totalGrossWt += avgGross * qty;
      totals.totalFineWt += (avgNet * wastage / 100) * qty;
      
      const lotSetMC = setMcBase + (setMcBase * setMakingPercent) / 100;
      const lotGramMC = avgNet * gramMcPerGram;
      const lotSpecMC = avgSpec * specMcPerGram;
      
      totals.totalMC += (lotSetMC + lotGramMC + lotSpecMC) * qty;
    });
  }
  return totals;
};

/**
 * Calculate order-level metrics (fineWeight and totalMc).
 * @param {Object} product - Order product object
 * @returns {{ fineWeight: number, totalMc: number }}
 */
export const calculateOrderMetrics = (product) => {
  const n = num;
  const wastage = calculateWastage(product);
  const setMc = calculateSetMc(product);
  const gramMcPerGram = calculateGramMcPerGram(product);
  const specMcPerGram = calculateSpecMcPerGram(product);
  
  const lotNet = (product.lotDetails || []).reduce((sum, l) => {
    const variant = (product.selectedVariants || []).find((v) => v.size === l.size);
    const qty = n(l.quantity) || n(variant?.quantity) || n(l.set) || 1;
    return sum + n(l.avgNetWt) * qty;
  }, 0);
  const lotSpec = (product.lotDetails || []).reduce((sum, l) => {
    const variant = (product.selectedVariants || []).find((v) => v.size === l.size);
    const qty = n(l.quantity) || n(variant?.quantity) || n(l.set) || 1;
    return sum + n(l.avgSpecWt) * qty;
  }, 0);
  const effectiveNetWt = lotNet > 0 ? lotNet : n(product.netWt);
  const effectiveSpecWt = lotSpec > 0 ? lotSpec : n(product.specificationWt);
  
  const stampCertCost = calculateStampCertificateCost(product);
  
const TotalAmount = setMc + gramMcPerGram * effectiveNetWt + specMcPerGram * effectiveSpecWt + stampCertCost;
   const fineWeight = (effectiveNetWt * wastage) / 100;
   
   return {
     fineWeight: formatNumber(fineWeight, 3),
     totalMc: formatNumber(TotalAmount, 2),
   };
};

/**
 * Build size-wise breakdown for an order.
 * @param {Object} order - Order object
 * @returns {Object} Object containing rows, totals, locked, showSpecWt, approx, stampCostPerUnit
 */
export const calculateOrderBreakdown = (order) => {
  const n = num;
  const isPlane = (order.specification || "").toUpperCase() === "PLANE";
  const showSpecWt = !isPlane;
  const locked = !!(order.orderDetail && Object.keys(order.orderDetail).length > 0);
  
  const wastage = calculateWastage(order);
  const stampCostPerUnit = n(order.stampCertificateCost);
  let rows = [];
  
  if (locked) {
    const detail = order.orderDetail || {};
    rows = Object.entries(detail).map(([size, d]) => {
      const qty = n(d.qty);
      const gross = n(d.grossWT) * qty;
      const net = n(d.netwt) * qty;
      const spec = n(d.specWT) * qty;
      const fine = (net * wastage) / 100;
      const stampQty = n(d.stampCertificateQtyPerSet);
      const stampingCost = stampQty * qty * stampCostPerUnit;
      return { size, qty, gross, net, spec, fine, stampQty, stampingCost, approx: false };
    });
  } else if (Array.isArray(order.lotDetails) && Array.isArray(order.selectedVariants)) {
    rows = order.selectedVariants
      .map((variant) => {
        const lot = order.lotDetails.find((l) => l.size === variant.size);
        if (!lot) return null;
        const qty = n(variant.quantity);
        const gross = n(lot.avgGrossWt) * qty;
        const net = n(lot.avgNetWt) * qty;
        const spec = n(lot.avgSpecWt) * qty;
        const fine = (net * wastage) / 100;
        const stampQty = n(order.stampCertificateQtyPerSet);
        const stampingCost = stampQty * qty * stampCostPerUnit;
        return { size: variant.size, qty, gross, net, spec, fine, stampQty, stampingCost, approx: true };
      })
      .filter(Boolean);
  }
  
  const totals = rows.reduce(
    (acc, r) => {
      acc.totalGross += r.gross;
      acc.totalNet += r.net;
      acc.totalSpec += r.spec;
      acc.totalFine += r.fine;
      acc.totalQty += r.qty;
      acc.totalStampQty += r.stampQty * r.qty;
      acc.totalStampingCost += r.stampingCost;
      return acc;
    },
    { totalGross: 0, totalNet: 0, totalSpec: 0, totalFine: 0, totalQty: 0, totalStampQty: 0, totalStampingCost: 0 }
  );
  
  return { rows, totals, locked, showSpecWt, approx: !locked, stampCostPerUnit };
};

/**
 * Calculate order summary from breakdown.
 * Used for single-order weight summary.
 * @param {Object} order - Order object
 * @returns {Object} Object containing gross, net, fine, qty, approx
 */
export const calculateOrderSummary = (order, calculateOrderBreakdown) => {
  const n = num;
  const breakdown = calculateOrderBreakdown(order);
  const hasSizes = breakdown.rows.length > 0;
  
  const wastage = calculateWastage(order);
  const isLotBased = Array.isArray(order.lotDetails) && order.lotDetails.length > 0;
  
  if (hasSizes) {
    return {
      gross: breakdown.totals.totalGross,
      net: breakdown.totals.totalNet,
      fine: breakdown.totals.totalFine,
      qty: breakdown.totals.totalQty,
      approx: breakdown.approx,
    };
  }
  
  const qty = order.quantity || 1;
  const lotQty = (l) => {
    const variant = (order.selectedVariants || []).find((v) => v.size === l.size);
    return n(variant?.quantity) || n(l.set) || 1;
  };
  let gross, net;
  gross = order.lotDetails.reduce((s, l) => s + n(l.avgGrossWt) * lotQty(l), 0);
  net = order.lotDetails.reduce((s, l) => s + n(l.avgNetWt) * lotQty(l), 0);
  return { gross, net, fine: (net * wastage) / 100, qty, approx: isLotBased };
};

/**
 * Format a number safely with specified decimal places.
 * Handles null/undefined and ensures consistent formatting.
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {number} Formatted number
 */
export const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return 0;
  return parseFloat(parseFloat(num).toFixed(decimals));
};

/**
 * Format a number as currency with ₹ symbol.
 * Handles null/undefined and ensures consistent formatting.
 * @param {number} num - The number to format as currency
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (num, decimals = 2) => {
  const formatted = formatNumber(num, decimals);
  return `₹${formatted}`;
};

/**
 * Calculate discount amount from original price and discount percentage.
 * Handles null/undefined and ensures consistent formatting.
 * @param {number} originalPrice - Original price
 * @param {number} discountPercent - Discount percentage (e.g., 10 for 10%)
 * @returns {number} Discount amount
 */
export const calculateDiscount = (originalPrice, discountPercent) => {
  const price = formatNumber(originalPrice, 2);
  const discount = formatNumber(discountPercent, 2);
  return (price * discount) / 100;
};

/**
 * Calculate total after discount from original price and discount percentage.
 * Handles null/undefined and ensures consistent formatting.
 * @param {number} originalPrice - Original price
 * @param {number} discountPercent - Discount percentage (e.g., 10 for 10%)
 * @returns {number} Total after discount
 */
export const calculateDiscountedTotal = (originalPrice, discountPercent) => {
  const price = formatNumber(originalPrice, 2);
  const discount = formatNumber(discountPercent, 2);
  return price - (price * discount) / 100;
};

/**
 * Create a simplified lot detail object from size, maxset, netGm, and min.
 * Returns only avgNetWt, size, and set.
 * Formula: avgNetWt = netGm / min
 * @param {string} size - Size identifier
 * @param {number|string} maxset - Set count
 * @param {number|string} netGm - Net weight in grams
 * @param {number|string} min - Divisor (minimum count)
 * @returns {{ avgNetWt: string, size: string, set: number|string }}
 */
export const createLotDetail = (size, maxset, netGm, min) => {
  const avgNetWt = (num(netGm) / num(min)).toFixed(3);
  return {
    avgNetWt,
    size,
    set: maxset
  };
};

/**
 * Calculate return metrics (fineWeight and totalMc) for a single order piece.
 * @param {Object} order - The order object with specification fields
 * @param {number} netWt - Net weight of the piece being returned
 * @returns {{ fineWeight: number, totalMc: number }}
 */
export const calcReturnMetrics = (order, netWt) => {
  const n = num;
  const wastage = calculateWastage(order);
  const setMc = calculateSetMc(order);
  const gramMcPerGram = calculateGramMcPerGram(order);
  const specMcPerGram = calculateSpecMcPerGram(order);
  
  const specWt = n(order.specificationWt);
  
  const fineWeight = formatNumber((netWt * wastage) / 100, 3);
  const totalMc = formatNumber(setMc + gramMcPerGram * netWt + specMcPerGram * specWt, 2);
  
  return { fineWeight, totalMc };
};